import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { getAuth, onAuthStateChanged, signOut } from 'firebase/auth';
import { BrowserRouter as Router, Route, Routes, Navigate, useLocation } from 'react-router-dom'; // Import React Router
import Login from './Login';
import Dashboard from './Dashboard';
import ScenarioSelectionNew from './ScenarioSelection';
import CustomScenarioForm from './CustomScenarioForm';
import Simulator from './Simulator'; // Import Simulator page
import CustomScenariosPage from './CustomScenariosPage';
import LeadershipTrends from './LeadershipTrends';
import UserProfile from './UserProfile';
import ForgotPassword from './ForgotPassword';
import ResetPassword from './ResetPassword';
import jwtDecode from 'jwt-decode';
import HelpPage from './HelpPage';
import NotFound from './NotFound';
import AdminHomePage from './AdminHomePage';
import SubscriptionManagement from './SubscriptionManagement'
import { AppLayout } from './AppLayout';
import ReportPage from './ReportPage';
import UserConversationsPage from './UserConversationsPage';
import PricingPage from './PricingPage.js';
import UpgradeSuccess from './UpgradeSuccess.js';
import AdminUserImport from './AdminUserImport';
import GroupAdminPage from './GroupAdminPage';
import AvatarTestPage from './AvatarTestPage';
import LoginEthos from './LoginEthos';

const api = axios.create({
  baseURL: `${process.env.REACT_APP_API_BASE_URL}/api`,
  headers: {
    'Content-Type': 'application/json'
  }
});

const ProtectedRoute = ({ token, children }) => {
  return token ? children : <Navigate to="/login" />;
};

//---------------------------------------
//    Function for Admin Page Security
//---------------------------------------
const ProtectedAdminRoute = ({ children }) => {
  const [isAdmin, setIsAdmin] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
      const checkAdminAndPlan = async () => {
          try {
              const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/admin/check-admin`, {
                  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
              });
              setIsAdmin(response.data.isAdmin);
          } catch (error) {
              setIsAdmin(false);
              navigate('/dashboard');
          }
      };
      checkAdminAndPlan();
  }, [navigate]);

  if (isAdmin === null) {
      return <p>Loading...</p>;
  }

  return isAdmin ? children : <Navigate to="/dashboard" />;
};

//---------------------------------------
// Function for Group Admin Page Security
//---------------------------------------
const ProtectedGroupAdminRoute = ({ children }) => {
  const [isGroupAdmin, setIsGroupAdmin] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const checkGroupAdmin = async () => {
      try {
        const res = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/group-admin/check`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        setIsGroupAdmin(res.data.isGroupAdmin);
      } catch (err) {
        setIsGroupAdmin(false);
        navigate('/dashboard');
      }
    };
    checkGroupAdmin();
  }, [navigate]);

  if (isGroupAdmin === null) return <p>Loading...</p>;

  return isGroupAdmin ? children : <Navigate to="/dashboard" />;
};




//---------------------------------------

//---------------------------------------
//   Function to handle timeout logouts
//---------------------------------------
function useInactivityTimeout(totalTimeout, warningPeriod, handleLogout, isLoggedIn, location, refreshTimeoutId) {
  const [isShowingWarning, setIsShowingWarning] = useState(false);
  const [countdown, setCountdown] = useState(warningPeriod / 1000);  // Countdown in seconds
  const timeoutId = useRef(null);
  const warningId = useRef(null);
  const countdownIntervalId = useRef(null);

  const runWarningTimeout = useCallback(() => {
    clearTimeout(warningId.current);
    warningId.current = setTimeout(() => {
      console.log("Warning timeout reached: Logging out user");
      handleLogout();
    }, warningPeriod);
  }, [handleLogout, warningPeriod]);

  // Starts the warning and final countdown
  const startWarning = useCallback(() => {
    if (isShowingWarning) {
      return;
    }
    
    setIsShowingWarning(true);
    setCountdown(warningPeriod / 1000);
    runWarningTimeout();

    // Clear any existing interval before starting a new one
    if (countdownIntervalId.current) {
      clearInterval(countdownIntervalId.current);
    }

    countdownIntervalId.current = setInterval(() => {
        setCountdown((prev) => {
            const next = prev - 1;
            if (next < 1) {  // Check if logged out state is true
              clearInterval(countdownIntervalId.current);
              countdownIntervalId.current = null;
              return 0;
            }
            return next;
        });
    }, 1000);
  }, [isShowingWarning, warningPeriod, runWarningTimeout]);

  // Resets the timer
  const resetTimer = () => {
    // Prevent timer reset on the login screen
    if (isShowingWarning || !isLoggedIn || location.pathname === '/login') {
      return;
    }

    clearTimeout(timeoutId.current);
    timeoutId.current = setTimeout(startWarning, totalTimeout);
  }

  // Cleanup function to clear timeouts
  const clearAllTimeouts = useCallback(() => {
      clearTimeout(timeoutId.current);
      clearTimeout(warningId.current);
      if (countdownIntervalId.current) {
        clearInterval(countdownIntervalId.current);
        countdownIntervalId.current = null;
      }
      if (refreshTimeoutId.current) {
        clearTimeout(refreshTimeoutId.current);
        refreshTimeoutId.current = null;
      }
  }, [refreshTimeoutId]);

  const handleActivity = useCallback(() => {
    resetTimer();
  }, [resetTimer]);
  
  useEffect(() => {
    document.addEventListener('mousedown', handleActivity);
    document.addEventListener('keypress', handleActivity);
    document.addEventListener('touchstart', handleActivity);
  
    return () => {
      document.removeEventListener('mousedown', handleActivity);
      document.removeEventListener('keypress', handleActivity);
      document.removeEventListener('touchstart', handleActivity);
    };
  }, [handleActivity]);

  return { isShowingWarning, setIsShowingWarning, resetTimer, countdown, clearAllTimeouts };
}

//-----------------------------
//     Main Function
//-----------------------------
function App() {
  const navigate = useNavigate();
  const location = useLocation(); 
  const auth = getAuth();
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const refreshTimeoutId = useRef(null);
  const [selectedScenario, setSelectedScenario] = useState(() => {
    try {
      const scenario = localStorage.getItem('selectedScenario');
      return scenario ? JSON.parse(scenario) : null;
    } catch (error) {
      console.error('Failed to parse scenario:', error);
      return null;
    }
  });

  // Function to refresh the token
  const refreshToken = useCallback(async () => {
    try {
      const user = auth.currentUser;
      if (user) {
        const newToken = await user.getIdToken(true); // Force refresh the token
        localStorage.setItem('token', newToken);
        setToken(newToken);
        console.log('token refreshed');

        // Decode the refreshed token to get its expiration time
        const decodedToken = jwtDecode(newToken);
        const tokenExpiryTime = decodedToken.exp * 1000; // Convert seconds to milliseconds
        const currentTime = Date.now();
      
        // Calculate when to refresh the token next (5 minutes before it expires)
        const timeToRefresh = tokenExpiryTime - currentTime - 5 * 60 * 1000;

        if (timeToRefresh > 0) {
          refreshTimeoutId.current = setTimeout(refreshToken, timeToRefresh); // Set the next timeout
        }
      }
    } catch (error) {
      console.error('Error refreshing token:', error);
    }
  }, [auth]);
  
  const handleLogout = useCallback(() => {
    signOut(auth)
      .then(() => {
        console.log('User logged out');
        setIsLoggedIn(false);
        setToken(null);
        localStorage.clear();
        clearAllTimeouts();
        navigate('/login', { replace: true });
      })
      .catch((error) => console.error('Error logging out:', error));
  }, [navigate]);

  const {
    isShowingWarning,
    setIsShowingWarning,
    resetTimer,
    countdown,
    clearAllTimeouts,
  } = useInactivityTimeout(900000, 20000, handleLogout, isLoggedIn, location, refreshTimeoutId);


  //--------------------------------
  // Plan Check for Free or Paid
  //--------------------------------
  useEffect(() => {
    const fetchUserPlan = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/admin/get-plan`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        console.log("✅ User plan retrieved:", response.data.plan);
        localStorage.setItem("plan", response.data.plan || "paid");
      } catch (error) {
        console.error("❌ Failed to fetch user plan:", error.message);
        localStorage.setItem("plan", "paid");
      }
    };
  
    fetchUserPlan();
  }, []);
  

  //-------------------------
  // Timeout Related Code
  //-------------------------
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        setIsLoggedIn(true);

      // Retrieve and store the initial token on login
      user.getIdToken().then((idToken) => {
        localStorage.setItem('token', idToken);
        setToken(idToken);
        console.log('token set');
        // Decode the token to get its expiration time
        const decodedToken = jwtDecode(idToken);
        const tokenExpiryTime = decodedToken.exp * 1000; // Convert seconds to milliseconds
        const currentTime = Date.now();

        // Calculate when to refresh the token (e.g., 5 minutes before expiration)
        const timeToRefresh = tokenExpiryTime - currentTime - 5 * 60 * 1000;

        if (timeToRefresh > 0) {
          refreshTimeoutId.current = setTimeout(refreshToken, timeToRefresh);
        }
      });

      // Call resetTimer only on initial login, not on every state change
        if (!isShowingWarning) {
          resetTimer();
        }
      } else {
        setIsLoggedIn(false);
        clearAllTimeouts(); // Clear timers on logout
      }
    });

    return () => unsubscribe();
  }, [auth]);

  useEffect(() => {
    if (isLoggedIn && location.pathname !== '/login') {
      resetTimer(); // Start timer once navigation completes
    }
    // This effect only cares about the page changing
  }, [location.pathname]); 
  
  // Prevent modal and timers on login page
  useEffect(() => {
    if (location.pathname === '/login') {
      setIsShowingWarning(false); // Hide warning on login page
      clearAllTimeouts(); // Stop timers
    }
  }, [location, clearAllTimeouts, setIsShowingWarning]);

  useEffect(() => {
    if (!isShowingWarning && isLoggedIn && location.pathname !== '/login') {
      resetTimer();
    }
  }, [isShowingWarning]);

  const handleContinueSession = () => {
    setIsShowingWarning(false);
    clearAllTimeouts();
    resetTimer();
  };

  //---------------------------------------
  // Save selected scenario to localStorage
  //---------------------------------------
  useEffect(() => {
    if (selectedScenario) {
      localStorage.setItem('selectedScenario', JSON.stringify(selectedScenario));
    }
  }, [selectedScenario]);
  //---------------------------------------

  return (
    <div>
      {/* Now, useLocation is called within Router */}

      <div>
        <div>
          {isShowingWarning && (
            <div
              onClick={(e) => e.stopPropagation()}
              className="fixed inset-0 flex items-center justify-center z-[1000]"
            >
              <div className="bg-white p-8 rounded-xl shadow-lg w-80 text-center">
                <p className="text-lg text-gray-800 mb-6">
                  You will be logged out in <span className="font-bold">{countdown}</span> seconds due to inactivity.
                </p>

                <div className="flex justify-around mt-4">
                  <button
                    onClick={handleContinueSession}
                    className="px-5 py-2 text-white bg-green-600 hover:bg-green-700 rounded shadow-md transition-colors"
                  >
                    Stay Logged In
                  </button>
                  <button
                    onClick={handleLogout}
                    className="px-5 py-2 text-white bg-red-600 hover:bg-red-700 rounded shadow-md transition-colors"
                  >
                    Logout
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        <Routes>
          {/* Public routes (no sidebar) */}
          <Route path="/" element={<Navigate to={token ? "/dashboard" : "/login"} />} />
          <Route path="/login" element={<Login setToken={setToken} />} />
          <Route path="/ethos-login" element={<LoginEthos setToken={setToken} />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />

          {/* Sidebar layout route (nested) */}
          <Route element={<AppLayout />}>
            <Route path="/dashboard" element={<ProtectedRoute token={token}><Dashboard /></ProtectedRoute>} />
            <Route path="/scenarios" element={<ProtectedRoute token={token}><ScenarioSelectionNew setSelectedScenario={setSelectedScenario} /></ProtectedRoute>} />
            <Route path="/create-scenario/:scenarioId?" element={<ProtectedRoute token={token}><CustomScenarioForm /></ProtectedRoute>} />
            <Route path="/custom-scenarios" element={<ProtectedRoute token={token}><CustomScenariosPage /></ProtectedRoute>} />
            <Route path="/customScenarioForm/:scenarioId?" element={<ProtectedRoute token={token}><CustomScenarioForm /></ProtectedRoute>} />
            <Route path="/simulator" element={<ProtectedRoute token={token}><Simulator /></ProtectedRoute>} />
            <Route path="/profile" element={<ProtectedRoute token={token}><UserProfile /></ProtectedRoute>} />
            <Route path="/help" element={<ProtectedRoute token={token}><HelpPage /></ProtectedRoute>} />
            <Route path="/analytics" element={<ProtectedRoute token={token}><LeadershipTrends /></ProtectedRoute>} />
            <Route path="/subscription" element={<ProtectedRoute token={token}><SubscriptionManagement /></ProtectedRoute>} />
            <Route path="/report/:id" element={<ProtectedRoute token={token}><ReportPage /></ProtectedRoute>} />
            <Route path="/convo" element={<ProtectedRoute token={token}><UserConversationsPage /></ProtectedRoute>} />
            <Route path="/pricing" element={<ProtectedRoute token={token}><PricingPage /></ProtectedRoute>} />
            <Route path="/upgrade-success" element={<ProtectedRoute token={token}><UpgradeSuccess /></ProtectedRoute>} />
            <Route path="/avatar-test" element={<ProtectedRoute token={token}><AvatarTestPage /></ProtectedRoute>} />

            <Route path="/admin" element={<ProtectedAdminRoute><AdminHomePage /></ProtectedAdminRoute>} />
            <Route path="/admin-ui" element={<ProtectedAdminRoute><AdminUserImport /></ProtectedAdminRoute>} />
            <Route path="/group-admin-dashboard" element={<ProtectedGroupAdminRoute><GroupAdminPage /></ProtectedGroupAdminRoute>} />
          </Route>

          <Route path="*" element={<NotFound />} />
        </Routes>
      </div>
    </div>
  );
}

export default App;
